import workIntervalRepository from '@/domain/repositories/workIntervalRepository';
import { filterByDateRange } from '@/i12e/utilities/filterHelpers';
import dayjs from 'dayjs';

const workIntervalService = new EventTarget();
let localReadModel = null;
let localReadPromise = null;
let unsubscribeLocalChanges = () => {};
let lastCachedWorkIntervals = null;

const cacheLocalWorkIntervals = (workIntervals) => {
  // Shared server sync can deliver the same snapshot through both the change
  // notification and the initiating call; apply that object only once.
  if (workIntervals !== lastCachedWorkIntervals) {
    workIntervalRepository.replaceCache(workIntervals);
    lastCachedWorkIntervals = workIntervals;
  }
  return workIntervals;
};

const reconcileLocalReadModel = () =>
  localReadModel
    ? localReadModel.syncFromServer().then((workIntervals) => {
        cacheLocalWorkIntervals(workIntervals);
        return workIntervals;
      })
    : Promise.resolve();

const applyLocalWorkIntervalChanges = ({ changes, workIntervals }) => {
  const intervalsById = Object.fromEntries(
    workIntervals.map((interval) => [interval.id, interval])
  );

  changes.forEach(({ entity_id: intervalId, op }) => {
    if (op === 'delete') {
      workIntervalRepository.removeCachedWorkInterval(intervalId);
      workIntervalService.dispatchEvent(
        new CustomEvent('workIntervals:deleted', { detail: { id: intervalId } })
      );
      return;
    }

    const interval = intervalsById[intervalId];
    if (!interval) return;

    workIntervalRepository.upsertCachedWorkInterval(interval);
    workIntervalService.dispatchEvent(
      new CustomEvent(
        op === 'create' ? 'workIntervals:created' : 'workIntervals:updated',
        { detail: interval }
      )
    );
  });
};

Object.assign(workIntervalService, {
  /**
   * Retrieves all work intervals.
   * @returns {Promise<Array>} - A promise that resolves to an array of work intervals.
   */
  getAllWorkIntervals: () => {
    if (!localReadModel) {
      return workIntervalRepository.getAllWorkIntervals();
    }

    if (!localReadPromise) {
      localReadPromise = localReadModel
        .syncFromServer()
        .then((workIntervals) => {
          cacheLocalWorkIntervals(workIntervals);
          return workIntervals;
        })
        .finally(() => {
          localReadPromise = null;
        });
    }

    return localReadPromise;
  },

  /**
   * Reads persisted intervals without waiting for remote revalidation.
   *
   * @returns {Promise<Array>} Local work intervals.
   */
  readLocalWorkIntervals: () => {
    if (!localReadModel) {
      return workIntervalRepository.getAllWorkIntervals();
    }

    return localReadModel
      .readLocalWorkIntervals()
      .then(cacheLocalWorkIntervals);
  },

  /**
   * Revalidates local intervals through the shared synchronization coordinator.
   *
   * @returns {Promise<Array>} Authoritatively refreshed work intervals.
   */
  syncWorkIntervalsFromServer: () => {
    if (!localReadModel) {
      return workIntervalRepository.getAllWorkIntervals();
    }

    if (!localReadPromise) {
      localReadPromise = localReadModel
        .syncFromServer()
        .then(cacheLocalWorkIntervals)
        .finally(() => {
          localReadPromise = null;
        });
    }

    return localReadPromise;
  },

  /** @returns {Object} Shared local synchronization state. */
  getWorkIntervalSyncState: () =>
    localReadModel
      ? localReadModel.getSyncState()
      : { status: 'idle', error: null },

  /**
   * Subscribes to shared work-interval synchronization state changes.
   * @param {Function} listener - State change callback.
   * @returns {Function} Unsubscribe function.
   */
  subscribeWorkIntervalSyncState: (listener) => {
    if (!localReadModel) {
      return () => {};
    }
    return localReadModel.subscribeSyncState(listener);
  },

  /**
   * Registers the local read model for migrated interval consumers.
   *
   * @param {Object|null} readModel - Local read model or null on teardown.
   */
  setWorkIntervalLocalReadModel: (readModel) => {
    unsubscribeLocalChanges();
    localReadModel = readModel;
    localReadPromise = null;
    lastCachedWorkIntervals = null;
    unsubscribeLocalChanges = readModel?.subscribeChanges
      ? readModel.subscribeChanges((changeSet) => {
          applyLocalWorkIntervalChanges(changeSet);
        })
      : () => {};
  },

  /**
   * Updates all work intervals by fetching from the API.
   * @returns {Promise<Array>} - A promise that resolves to an array of work intervals.
   */
  updateAllWorkIntervals: () => {
    return workIntervalRepository
      .updateAllWorkIntervals()
      .then((workIntervals) => {
        workIntervalService.dispatchEvent(
          new CustomEvent('workIntervals:full-update', {
            detail: workIntervals
          })
        );
        return workIntervals;
      })
      .catch((error) => {
        console.error('Failed to update all work intervals:', error);
        throw error;
      });
  },

  /**
   * Creates a new work interval.
   * @param {Object} workIntervalData - The data for the new work interval.
   * @returns {Promise<Object>} - A promise that resolves to the new work interval object.
   */
  createWorkInterval: (workIntervalData) => {
    return workIntervalRepository
      .createWorkInterval(workIntervalData)
      .then((newWorkInterval) => {
        return reconcileLocalReadModel().then(() => {
          workIntervalService.dispatchEvent(
            new CustomEvent('workIntervals:created', {
              detail: newWorkInterval
            })
          );
          return newWorkInterval;
        });
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Updates an existing work interval.
   * @param {number|string} intervalId - The ID of the work interval to update.
   * @param {Object} updates - An object containing the fields to update.
   * @returns {Promise<Object>} - A promise that resolves to the updated work interval object.
   */
  updateWorkInterval: (intervalId, updates) => {
    const workInterval = workIntervalRepository.getWorkIntervalById(intervalId);
    if (!workInterval) {
      return Promise.reject(
        new Error(`Work interval with ID ${intervalId} not found`)
      );
    }
    return workIntervalRepository
      .updateWorkInterval(intervalId, updates)
      .then((updatedWorkInterval) => {
        return reconcileLocalReadModel().then(() => {
          workIntervalService.dispatchEvent(
            new CustomEvent('workIntervals:updated', {
              detail: updatedWorkInterval
            })
          );
          return updatedWorkInterval;
        });
      })
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Deletes a work interval online and reconciles the local read model.
   *
   * @param {number|string} intervalId - Work interval identifier.
   * @returns {Promise<void>} Resolves after local reconciliation completes.
   */
  deleteWorkInterval: (intervalId) => {
    const workInterval = workIntervalRepository.getWorkIntervalById(intervalId);
    if (!workInterval) {
      return Promise.reject(
        new Error(`Work interval with ID ${intervalId} not found`)
      );
    }

    return workIntervalRepository
      .deleteWorkInterval(intervalId)
      .then(() => reconcileLocalReadModel())
      .then(() => undefined)
      .catch((error) => {
        throw error;
      });
  },

  /**
   * Retrieves a work interval by its ID.
   * @param {number|string} intervalId - The ID of the work interval to retrieve.
   * @returns {Object|null} - The work interval object or null if not found.
   */
  getWorkIntervalById: (intervalId) => {
    return workIntervalRepository.getWorkIntervalById(intervalId);
  },

  /**
   * Retrieves all work intervals within a specific date range.
   * @param {Date} startDate - The start date.
   * @param {Date} endDate - The end date.
   * @returns {Promise<Array>} - An array of work intervals within the date range.
   */
  getWorkIntervalsInRange: (startDate, endDate) => {
    const read = localReadModel
      ? workIntervalService.readLocalWorkIntervals()
      : workIntervalRepository.getAllWorkIntervals();

    return read.then((workIntervals) => {
      const startUTC = dayjs(startDate);
      const endUTC = dayjs(endDate);
      return filterByDateRange(
        workIntervals,
        ['started_at', 'ended_at'],
        startUTC,
        endUTC
      );
    });
  },

  /**
   * Retrieves all work intervals associated with a specific task ID.
   * @param {number|string} taskId - The task ID.
   * @returns {Promise<Array>} - An array of work intervals for the task.
   */
  getWorkIntervalsByTaskId: (taskId) => {
    return workIntervalRepository
      .getAllWorkIntervals()
      .then((workIntervals) => {
        return workIntervals.filter((interval) => interval.task_id === taskId);
      });
  }
});

export default workIntervalService;
